import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Mail, ShieldCheck } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const SUPPORT_EMAIL = "support@fitauraai.com";

const DeleteAccount = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Delete Your Account — FitAura AI"
        description="How to delete your FitAura AI account and what data is removed or retained."
      />
      <div className="relative min-h-screen bg-background text-foreground">
        <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[140px]" />
        </div>

        <header className="sticky top-0 z-30 border-b border-white/5 bg-background/85 px-5 pt-[max(env(safe-area-inset-top),10px)] pb-3 backdrop-blur-xl">
          <div className="mx-auto flex w-full max-w-2xl items-center gap-2.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
              aria-label="Back"
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-primary/30 to-accent/20 ring-1 ring-primary/30 shadow-[0_0_18px_-4px_hsl(var(--primary)/0.55)]">
              <img src="/app-icon-128.png" alt="FitAura AI" className="h-full w-full object-cover" />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold leading-tight">Delete your account</h1>
              <p className="truncate text-xs text-muted-foreground">Permanent account &amp; data removal</p>
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-2xl px-5 pb-24 pt-6">
          <Card className="border-white/5 bg-card/60 p-5 backdrop-blur-xl">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-destructive/15 ring-1 ring-destructive/30">
                <Trash2 className="h-5 w-5 text-destructive" />
              </span>
              <div>
                <h2 className="text-lg font-semibold">Delete from inside the app</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  The fastest way to delete your FitAura AI account is from your profile:
                </p>
                <ol className="mt-3 space-y-1.5 text-sm text-foreground/90 list-decimal pl-5">
                  <li>Open the FitAura AI app and sign in.</li>
                  <li>Go to <span className="font-medium">Profile</span> (bottom tab).</li>
                  <li>Tap <span className="font-medium">Settings</span>.</li>
                  <li>Scroll down and tap <span className="font-medium">Delete account</span>.</li>
                  <li>Confirm. Your account is queued for permanent deletion.</li>
                </ol>
              </div>
            </div>
          </Card>

          <Card className="mt-4 border-white/5 bg-card/60 p-5 backdrop-blur-xl">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/15 ring-1 ring-primary/30">
                <Mail className="h-5 w-5 text-primary" />
              </span>
              <div>
                <h2 className="text-lg font-semibold">Or request by email</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Can't access the app? Email us from the address you signed up with:
                </p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20FitAura%20AI%20account`}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm font-medium text-primary ring-1 ring-primary/30 hover:bg-primary/15"
                >
                  <Mail className="h-4 w-4" />
                  {SUPPORT_EMAIL}
                </a>
                <p className="mt-3 text-xs text-muted-foreground">
                  We process email deletion requests within 7 business days.
                </p>
              </div>
            </div>
          </Card>

          <Card className="mt-4 border-white/5 bg-card/60 p-5 backdrop-blur-xl">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-500/15 ring-1 ring-emerald-500/30">
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
              </span>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold">What gets deleted</h2>
                <ul className="mt-2 space-y-1.5 text-sm text-foreground/90 list-disc pl-5">
                  <li>Your profile, name, gender, body measurements and onboarding answers</li>
                  <li>Your uploaded body photos and selfies</li>
                  <li>All generated try-on images and outfit results</li>
                  <li>Your saved looks, history and favorites</li>
                  <li>Remaining free-tier and paid credit balance</li>
                  <li>Sign-in identity (email / Google) linked to FitAura AI</li>
                </ul>

                <h3 className="mt-5 text-sm font-semibold">What we keep (and why)</h3>
                <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
                  <li>
                    A one-way hash of your email for up to <span className="font-medium text-foreground/90">90 days</span> — prevents
                    abuse of free credits by repeated sign-ups.
                  </li>
                  <li>
                    Purchase &amp; refund records required by Google Play, tax and accounting law (typically up to 7 years).
                  </li>
                  <li>
                    Anonymous, aggregated analytics that cannot be linked back to you.
                  </li>
                </ul>

                <p className="mt-5 text-xs text-muted-foreground">
                  Deletion is permanent and cannot be undone. After deletion you can create a new
                  account with the same email, but past try-ons and credits will not be restored.
                </p>
              </div>
            </div>
          </Card>
        </main>
      </div>
    </>
  );
};

export default DeleteAccount;
